import React, { useEffect, useState } from "react";
import { useField } from "formik";
import { Button, Grid, makeStyles } from "@material-ui/core";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaRegImage } from "react-icons/fa6";
import { useDropzone } from "react-dropzone";

const useStyles = makeStyles((theme) => ({
    img: {
        objectFit: "cover",
        objectPosition: "center",
        borderRadius: "50%",
    },
    btn: {
        height: "40px",
    },
    imgUploadBtn: {
        display: "flex",
        gap: `${0.25 * 3}rem`,
        [theme.breakpoints.up('xs')]: {
            justifyContent: "start",
        },
        [theme.breakpoints.up('md')]: {
            justifyContent: "end",
        },
    },
    previewContainer: {
        [theme.breakpoints.up('xs')]: {
            marginBottom: `${0.25 * 5}rem`,
        },
        [theme.breakpoints.up('md')]: {
            marginBottom: `0`,
        },
    }
}));

export default function DropZoneWithPreview({
    filefield,
    setFieldValue,
    setFile,
    values,
    uploadBtnText = "upload",
}) {
    const classes = useStyles();
    const { features } = useSelector((state) => state.plan);
    const [files, setFiles] = useState([]);

    const maxSize = 2 * 1024 * 1024;

    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            "image/png": [".png"],
            "image/jpeg": [".jpeg", ".jpg"],
        },
        multiple: false,
        maxSize: maxSize,
        onDrop: (acceptedFiles) => {
            if (features["logo"].allowed === 0 && filefield === "white_label_logo") {
                toast.dismiss();
                toast.error(<div><b>Feature unavailable.</b> <br></br>Please contact your account manager to upgrade your plan.</div>);
                return;
            }
            if (features["background_img"].allowed === 0 && filefield === "background_image") {
                toast.dismiss();
                toast.error(<div><b>Feature unavailable.</b> <br></br>Please contact your account manager to upgrade your plan.</div>);
                return;
            }

            setFiles(
                acceptedFiles.map((file) =>
                    Object.assign(file, {
                        preview: URL.createObjectURL(file),
                    })
                )
            );

            const filename = `${new Date().getTime()}-${acceptedFiles[0].name}`;
            const file = new File([acceptedFiles[0]], filename);
            setFieldValue(filefield, filename);
            setFile([file]);
        },
    });

    useEffect(() => {
        // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
        return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
    }, [files]);

    const [meta] = useField(filefield);

    function handleDeleteAddFile() {
        setFiles([]);
        setFieldValue(filefield, "");
        setFile();
    }

    return (
        <Grid container>
            <Grid item xs={12} md={6}>
                <Grid container alignItems="center" spacing={3} className={`${classes.previewContainer}`}>
                    <Grid item>
                        {files.length === 0 ? (
                            values[filefield] ? (
                                <img
                                    className={classes.img}
                                    alt="logo"
                                    src={`${process.env.REACT_APP_ENV === "production"?"https://assets.diamondsoncall.com":"https://alpha.diamondsupload.com"}/assets/white_label/${values[filefield]}`}
                                    height={110}
                                    width={110}
                                />
                            ) : (
                                <FaRegImage size={110} />
                            )
                        ) : (
                            <img
                                className={classes.img}
                                alt="logo"
                                src={files[0].preview}
                                onLoad={() => {
                                    URL.revokeObjectURL(files[0].preview);
                                }}
                                height={110}
                                width={110}
                            />
                        )}
                    </Grid>
                    <Grid item>
                        <div className="d-flex flex-column">
                            <span className="font-size-lg font-weight-bold">
                                {values[filefield]
                                    ? values[filefield]
                                    : "Image Name"}
                            </span>
                            <span className="small">PNG, JPEG UNDER 2MB</span>
                        </div>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} md={6} style={{ alignSelf: "center" }}>
                <div className={`${classes.imgUploadBtn}`}>
                    <div {...getRootProps({ className: "dropzone" })}>
                        <input {...getInputProps()} />
                        <Button variant="outlined" className={classes.btn}>
                            {uploadBtnText}
                        </Button>
                    </div>
                    <Button
                        className={`${classes.btn} ml-4`}
                        variant="contained"
                        onClick={() => handleDeleteAddFile()}
                    >
                        delete
                    </Button>
                </div>
            </Grid>
            {meta.error ? <span>{meta.error}</span> : ""}
        </Grid>
    );
}
